import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import useMapbox, { zoomToEntities } from '@/composables/useMapbox'
import { useQueryParams } from '@/composables/useQueryParams'
import { useGeneralStore } from '@/pinia'
import { computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'


export default /*@__PURE__*/_defineComponent({
  __name: 'MapCamera',
  setup(__props) {

const store = useStore()
const piniaStore = useGeneralStore()
const { setQueryParam } = useQueryParams()

const { map } = useMapbox()

const selectedComplexId = computed<string | undefined>(
  () => store.getters['mapview/selectedComplexId']
)
const selectedRealEstateId = computed<string | undefined>(
  () => store.getters['mapview/selectedRealEstateId']
)
const selectedBuildingId = computed<string | undefined>(
  () => store.getters['mapview/selectedBuildingId']
)
const currentView = computed(() => store.state.currentView)
const primaryInstance = computed(() => store.state.indoorview?.primaryInstance)
const primaryInstancePosition = computed(() => store.state.indoorview?.primaryInstancePosition)
const primaryInstanceOrientation = computed(
  () => store.state.indoorview?.primaryInstanceOrientation
)
const selectedLevelNumber = computed(() => store.getters.selectedLevelNumber)
const selectedLevelId = computed(() => store.getters.selectedLevelId)
const selectedRoomId = computed(() => store.getters.selectedRoomId)
const selectedPOIId = computed(() => store.state.selectedPoi?.guid)
const selectedLineLayerId = computed(() => piniaStore.selectedLineLayerId || undefined)
const selectedRoomPosition = computed(() => store.getters.selectedRoomPosition)
const selectedZoneLayerId = computed(() => piniaStore.selectedZoneLayerId || undefined)
const isIndoorViewShowing = computed(() => store.getters.isIndoorviewShowing)
const isPlanViewShowing = computed(() => store.getters.isPlanviewShowing)
const isModelViewShowing = computed(() => store.getters.isModelviewShowing)

onMounted(() => {
  subscribeToListeners()
  if (selectedBuildingId.value) {
    zoomToEntities([selectedBuildingId.value], false)
  } else if (selectedRealEstateId.value) {
    zoomToEntities([selectedRealEstateId.value], false)
  } else if (selectedComplexId.value) {
    zoomToEntities([selectedComplexId.value], false)
  }
  return () => unsubscribeFromListeners()
})

function subscribeToListeners() {
  map?.on('moveend', handleMove)
  map?.on('movestart', handleMove)
  watch([selectedComplexId, selectedRealEstateId, selectedBuildingId], handleSelection)
  watch([selectedLevelNumber, selectedLevelId], handleLevelSelection)
  watch(selectedRoomId, handleRoomSelection)
  watch(selectedPOIId, handlePOISelection)
  watch(selectedLineLayerId, handleLineLayerSelection)
  watch(selectedRoomPosition, handlePosition)
  watch(selectedZoneLayerId, handleZoneLayerSelection)
  watch(isPlanViewShowing, handlePlanViewSelection)
  watch(isModelViewShowing, handleModelViewSelection)
  watch(isIndoorViewShowing, handleIndoorViewSelection)
  watch(primaryInstance, handlePrimaryInstance)
  watch(primaryInstancePosition, handlePosition)
  watch(primaryInstanceOrientation, handlePrimaryInstanceOrientation)
  watch(currentView, (curr, old) => handleViewChange(curr, old), { immediate: true })
}

function handleSelection([complexId, realEstateId, buildingId]: [string?, string?, string?]) {
  const defaults = {
    complex: undefined,
    realEstate: undefined,
    building: undefined,
    level: undefined,
    planview: undefined,
    modelview: undefined,
    indoorview: undefined
  }
  if (buildingId) {
    setQueryParam({
      ...defaults,
      building: `${buildingId}`
    })
  } else if (realEstateId) {
    setQueryParam({
      ...defaults,
      realEstate: `${realEstateId}`
    })
  } else if (complexId) {
    setQueryParam({
      ...defaults,
      complex: `${complexId}`
    })
  } else {
    setQueryParam(defaults)
  }
}

function unsubscribeFromListeners() {
  map?.off('moveend', handleMove)
  map?.off('movestart', handleMove)
}

function handleMove() {
  const center = map?.getCenter()
  if (center?.lng === undefined || center?.lat === undefined) {
    setQueryParam({ center: undefined })
  } else {
    setQueryParam({ center: `${center.lat},${center.lng}` })
  }
  const pitch = map?.getPitch()
  if (pitch === undefined) {
    setQueryParam({ pitch: undefined })
  } else {
    setQueryParam({ pitch: `${pitch}` })
  }
  const bearing = map?.getBearing()
  if (bearing === undefined) {
    setQueryParam({ bearing: undefined })
  } else {
    setQueryParam({ bearing: `${bearing}` })
  }
  const zoom = map?.getZoom()
  if (zoom === undefined) {
    setQueryParam({ zoom: undefined })
  } else {
    setQueryParam({ zoom: `${zoom}` })
    store.commit('SET_CURRENT_ZOOM', zoom)
  }
}

function handleLevelSelection([number, id]: [number?, string?]) {
  if (!number || !id) {
    setQueryParam({
      level: undefined,
      levelNumber: undefined,
      planview: undefined,
      modelview: undefined,
      indoorview: undefined
    })
  } else {
    setQueryParam({ levelNumber: `${number}` })
    setQueryParam({ level: `${id}` })
  }
}

function handleRoomSelection(value?: string) {
  if (!value) {
    setQueryParam({ room: undefined })
  } else {
    setQueryParam({ room: `${value}` })
  }
}

function handlePOISelection(value?: string) {
  if (!value) {
    setQueryParam({ poi: undefined })
  } else {
    setQueryParam({ poi: `${value}` })
  }
}

function handleLineLayerSelection(value?: string) {
  if (!value) {
    setQueryParam({ lineLayer: undefined })
  } else {
    setQueryParam({ lineLayer: `${value}` })
  }
}

function handlePosition(value?: number[]) {
  if (!value) {
    setQueryParam({ x: undefined, y: undefined, z: undefined })
  } else {
    setQueryParam({ x: `${value[0]}`, y: `${value[1]}`, z: `${value[2]}` })
  }
}

function handleIndoorViewSelection(value?: boolean) {
  if (!value) {
    setQueryParam({ indoorview: undefined })
  } else {
    setQueryParam({ indoorview: `${value}`, modelview: undefined, planview: undefined })
  }
}

function handleModelViewSelection(value?: boolean) {
  if (!value) {
    setQueryParam({ modelview: undefined })
  } else {
    setQueryParam({ modelview: `${value}`, indoorview: undefined, planview: undefined })
  }
}

function handlePlanViewSelection(value?: boolean) {
  if (!value) {
    setQueryParam({ planview: undefined })
  } else {
    setQueryParam({ planview: `${value}`, indoorview: undefined, modelview: undefined })
  }
}

function handlePrimaryInstance(value?: unknown) {
  if (typeof value !== 'number') {
    setQueryParam({ instance: undefined })
  } else {
    setQueryParam({ instance: `${value}` })
  }
}

function handlePrimaryInstanceOrientation(value?: any) {
  if (!value) {
    setQueryParam({ vlon: undefined, vlat: undefined })
  } else {
    setQueryParam({ vlon: `${value.lon}`, vlat: `${value.lat}` })
  }
}

function handleZoneLayerSelection(value?: string) {
  if (!value) {
    setQueryParam({ zoneLayer: undefined })
  } else {
    setQueryParam({ zoneLayer: `${value}` })
  }
}
function handleViewChange(curr?: string, old?: string) {
  if (old !== 'dashboard') {
    store.commit('SET_PREVIOUS_VIEW', { view: old })
  }
  if (curr === 'mapview') {
    store.commit('RESET_CURRENT_TOOL')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div"))
}
}

})